import React, { Component } from "react";
import { Link } from "react-router-dom";

// CSS
import "./Contact.scss";

// Components
import Header from "../../components/Header/Header";
import Footer from "../../components/Footer/Footer";
import Icon from "../../components/Icon";

// Helper
import { resolveTenant, cachedTenant } from "../../Helper/Api";
import { brandingOf } from "../../Helper/Branding";
import { t } from "../../Helper/i18n";

// Properties
const properties = require("../../properties.json");

// The reference design's Contact page had a message form too -- dropped by the teacher's own
// request (contact info + social links only), so this page is just the info card.
// Brand marks stay on FontAwesome: a logo is a logo, not something to redraw.
const SOCIAL_LINKS = [
  { key: "facebookUrl", icon: "fab fa-facebook-f", label: "Facebook" },
  { key: "instagramUrl", icon: "fab fa-instagram", label: "Instagram" },
  { key: "linkedinUrl", icon: "fab fa-linkedin-in", label: "LinkedIn" },
  { key: "behanceUrl", icon: "fab fa-behance", label: "Behance" },
];

// wa.me needs a bare international number -- no "+", spaces or a local leading 0.
function waNumber(mobile) {
  if (!mobile) return null;
  const digits = mobile.replace(/\D/g, "");
  if (!digits) return null;
  return digits.startsWith("20") ? digits : `20${digits.replace(/^0/, "")}`;
}

class Contact extends Component {
  state = {
    tenant: cachedTenant(),
    notFound: false,
    copied: null, // which detail was just copied
  };

  componentDidMount() {
    resolveTenant()
      .then((tenant) => {
        document.title = tenant.name ? `${t("contact")} | ${tenant.name}` : properties.site_name;
        this.setState({ tenant });
      })
      .catch(() => this.setState({ notFound: true }));
  }

  componentWillUnmount() {
    if (this.io) this.io.disconnect();
    if (this.copyTimer) window.clearTimeout(this.copyTimer);
  }

  watchReveal = (root) => {
    if (!root || this.io) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches || !("IntersectionObserver" in window)) return;

    root.classList.add("js-reveal");
    this.io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("is-in");
          this.io.unobserve(entry.target);
        });
      },
      { rootMargin: "0px 0px -10% 0px", threshold: 0.12 }
    );
    root.querySelectorAll("[data-reveal]").forEach((el) => this.io.observe(el));
  };

  /** Copying beats retyping an address off a screen. Clipboard access can be
   *  refused, so the button says nothing happened rather than claiming it did. */
  copy = (key, value) => {
    if (!navigator.clipboard) return;
    navigator.clipboard
      .writeText(value)
      .then(() => {
        this.setState({ copied: key });
        if (this.copyTimer) window.clearTimeout(this.copyTimer);
        this.copyTimer = window.setTimeout(() => this.setState({ copied: null }), 1800);
      })
      .catch(() => {});
  };

  render() {
    const { tenant, notFound, copied } = this.state;

    if (notFound) {
      return (
        <div className="page">
          <div className="container text-center">
            <p>{t("notFound")}</p>
          </div>
        </div>
      );
    }

    if (!tenant) return null;

    const teacherName = tenant.name || "";
    const b = brandingOf(tenant);
    const activeSocials = SOCIAL_LINKS.filter((s) => tenant[s.key]);
    const wa = waNumber(tenant.mobile);

    // Only what the teacher has actually filled in.
    const details = [
      tenant.email && {
        key: "email",
        icon: "mail",
        label: t("email"),
        value: tenant.email,
        href: `mailto:${tenant.email}`,
        action: t("sendEmail"),
        copyable: true,
      },
      tenant.mobile && {
        key: "phone",
        icon: "phone",
        label: t("phone"),
        value: tenant.mobile,
        href: `tel:${tenant.mobile.replace(/\s/g, "")}`,
        action: t("call"),
        copyable: true,
      },
      wa && {
        key: "wa",
        icon: "chat",
        label: t("whatsappLabel"),
        value: tenant.mobile,
        href: `https://wa.me/${wa}`,
        action: t("openWhatsapp"),
        external: true,
      },
      tenant.location && {
        key: "location",
        icon: "pin",
        label: t("location"),
        value: tenant.location,
      },
    ].filter(Boolean);

    return (
      <>
        <div className="ct-stage mu mu-ground">
          <div className="ct-sky" aria-hidden="true">
            <span className="aur a1"></span>
            <span className="aur a2"></span>
            <span className="dots"></span>
          </div>

          <Header tenant={tenant} onStage />

          <div className="ct-head">
            <span className="mu-chip"><Icon name="chat" className="chipi" /> {t("getInTouch")}</span>
            <h1>{t("contactName", { name: teacherName })}</h1>
            <p>{b.tagline ? b.tagline : t("contactLead")}</p>
          </div>
        </div>

        <div className="ct-body mu" ref={this.watchReveal}>
          {details.length > 0 ? (
            <div className="ct-grid">
              {details.map((d, i) => (
                <article className="ct-card" key={d.key} data-reveal style={{ "--i": i % 3 }}>
                  <span className="ct-ic"><Icon name={d.icon} /></span>

                  <span className="ct-label">{d.label}</span>
                  <span className="ct-value">{d.value}</span>

                  <div className="ct-acts">
                    {d.href && (
                      <a
                        className="mu-btn is-gold is-sm"
                        href={d.href}
                        {...(d.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                      >
                        {d.action}
                      </a>
                    )}
                    {d.copyable && (
                      <button
                        type="button"
                        className={`ct-copy ${copied === d.key ? "is-done" : ""}`}
                        onClick={() => this.copy(d.key, d.value)}
                      >
                        <Icon name={copied === d.key ? "check" : "copy"} />
                        {copied === d.key ? t("copied") : t("copy")}
                      </button>
                    )}
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="ct-empty" data-reveal>
              <span className="ic" aria-hidden="true"><Icon name="chat" /></span>
              <b>{t("noContactYet")}</b>
              <p>{t("noContactText", { name: teacherName })}</p>
              <Link to="/courses" className="mu-btn is-gold has-arw">
                {t("seeCourses")} <Icon name="arrow" className="arw" />
              </Link>
            </div>
          )}

          {activeSocials.length > 0 && (
            <div className="ct-social" data-reveal>
              <span className="ct-sociallabel">{t("elsewhere")}</span>
              <div className="ct-sociallinks">
                {activeSocials.map((s) => (
                  <a href={tenant[s.key]} target="_blank" rel="noopener noreferrer" key={s.key} aria-label={s.label}>
                    <i className={s.icon} aria-hidden="true"></i>
                    <span>{s.label}</span>
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>

        <Footer tenant={tenant} />
      </>
    );
  }
}

export default Contact;
